import type { RegionKey, USState } from '../types';
import { ACCOMMODATIONS, TASTING_STOPS } from './data';
import { REGIONS } from './regions';

export type RegionHubKey = RegionKey;

export interface TrailStateHub {
  state: USState;
  slug: string;
  title: string;
  eyebrow: string;
  subtitle: string;
  bestFor: string[];
  accent: string;
}

export interface TrailRegionHub {
  key: RegionKey;
  state: USState;
  title: string;
  shortTitle: string;
  subtitle: string;
  mood: string;
  stopIds: string[];
  stayIds: string[];
}

export const STATE_HUBS: TrailStateHub[] = [
  {
    state: 'TX',
    slug: 'texas-olive-chapter',
    title: 'Texas Olive Chapter',
    eyebrow: 'Hill Country tastings / river-town stays',
    subtitle:
      'The Texas leg remains the backbone of the route, with the strongest orchard and resort pairing in the current guide.',
    bestFor: ['Weekend tastings', 'Hill Country escapes', 'Food-first road trips', 'Stay-led planning'],
    accent: 'Olive, limestone, river blue',
  },
  {
    state: 'NM',
    slug: 'new-mexico-olive-chapter',
    title: 'New Mexico Olive Chapter',
    eyebrow: 'High-desert tastings / Plaza stays',
    subtitle:
      'New Mexico adds urban tasting depth through Santa Fe and Albuquerque rather than orchard density.',
    bestFor: ['Walkable tasting rooms', 'Santa Fe weekends', 'Classes', 'Culinary shopping'],
    accent: 'Adobe, chile, gold',
  },
  {
    state: 'AZ',
    slug: 'arizona-olive-chapter',
    title: 'Arizona Olive Chapter',
    eyebrow: 'Working mill / desert metro base',
    subtitle:
      'Arizona closes the route with a real mill visit in Queen Creek and Phoenix-area tasting-room support.',
    bestFor: ['Mill tours', 'Desert weekends', 'Metro stays', 'Arizona finish'],
    accent: 'Terracotta, olive, sun wash',
  },
];

export const REGION_HUBS: TrailRegionHub[] = REGIONS.map((region) => ({
  key: region.key,
  state: region.state,
  title: region.title,
  shortTitle: region.shortTitle,
  subtitle: region.subtitle,
  mood: region.mood,
  stopIds: TASTING_STOPS.filter((stop) => stop.region === region.key).map((stop) => stop.id),
  stayIds: ACCOMMODATIONS.filter((stay) => stay.region === region.key).map((stay) => stay.id),
}));

export function getRegionHubForLocation(locationId: string): TrailRegionHub | undefined {
  return REGION_HUBS.find(
    (hub) => hub.stopIds.includes(locationId) || hub.stayIds.includes(locationId),
  );
}

export function getStateHub(state: USState): TrailStateHub | undefined {
  return STATE_HUBS.find((hub) => hub.state === state);
}

export function getStateHubBySlug(slug: string): TrailStateHub | undefined {
  return STATE_HUBS.find((hub) => hub.slug === slug);
}

export function getStateHref(state: USState): string {
  const hub = getStateHub(state);
  return hub ? `/${hub.slug}` : '/';
}

export function getRegionsForState(state: USState): TrailRegionHub[] {
  return REGION_HUBS.filter((region) => region.state === state);
}
